import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import HeadingLayout from "src/components/base/heading-layout";
import { ComputerDesktopIcon, ServerStackIcon } from "@heroicons/react/20/solid";
import { ApiInternal } from "src/api/api_internal";
import { Table, TableBody, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { FullDevice } from "src/api/generated/frontend_internal_v1";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Listbox, ListboxLabel, ListboxOption } from "src/components/base/listbox";
import RenameDeviceDialog from "src/components/dialogs/rename-device";
import ExpireDeviceKeyDialog from "src/components/dialogs/expire-device-key";
import DeleteDeviceDialog from "src/components/dialogs/delete-device";
import Form from "src/components/base/form";
import TablePagination from "src/components/table-pagination";
import { useForm } from "@tanstack/react-form";
import { BadgeButton } from "src/components/base/badges/base";
import WS from "src/api/ws";
import { PrimaryButton } from "src/components/base/button";
import { DeviceRowAdmin } from "src/components/device-row-admin";

/**
 * The properties for {@link DeviceOverview}
 */
export type DeviceOverviewProps = {};

/**
 * Filter options
 */
type Filter = "all" | "server-only" | "clients-only";

const LIMIT = 25;

/**
 * An overview over all devices
 */
function DeviceOverview(props: DeviceOverviewProps) {
    const [t] = useTranslation("admin-device-overview");
    const [tg] = useTranslation();

    const navigate = useNavigate();
    const router = useRouter();

    const { page, filter, search } = Route.useSearch();

    const data = Route.useLoaderData();

    // Popups
    const [openRenameDevice, setOpenRenameDevice] = React.useState<FullDevice>();
    const [openExpireKey, setOpenExpireKey] = React.useState<FullDevice>();
    const [openDeleteDevice, setOpenDeleteDevice] = React.useState<FullDevice>();

    const form = useForm({
        defaultValues: {
            search: search,
        },
    });

    useEffect(() => {
        const listener = WS.addEventListener("message.DevicesChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <HeadingLayout
            heading={t("heading.device-overview")}
            headingDescription={t("description.device-overview")}
            headingChildren={
                <PrimaryButton href={"/a/devices/create-server"}>
                    <ServerStackIcon />
                    {t("button.add-server")}
                </PrimaryButton>
            }
        >
            <Form className={"grid grid-cols-2 gap-12"} onSubmit={form.handleSubmit}>
                <Fieldset>
                    <FieldGroup>
                        <form.Field
                            name={"search"}
                            validators={{
                                onChangeAsync: async ({ fieldApi }) => {
                                    await navigate({
                                        to: "/a/devices",
                                        search: {
                                            page: 1,
                                            filter,
                                            search: fieldApi.state.value,
                                        },
                                    });
                                },
                                onChangeAsyncDebounceMs: 500,
                            }}
                        >
                            {(fieldApi) => (
                                <Field>
                                    <Label>{t("label.filter")}</Label>
                                    <Input
                                        value={fieldApi.state.value}
                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                    />
                                </Field>
                            )}
                        </form.Field>
                    </FieldGroup>
                </Fieldset>

                <Fieldset className={"max-w-56"}>
                    <FieldGroup>
                        <Field>
                            <Label className={"w-full"}>{t("label.show-devices")}</Label>
                            <Listbox
                                value={filter}
                                onChange={async (x) => {
                                    await navigate({
                                        to: "/a/devices",
                                        search: {
                                            page: 1,
                                            filter: x,
                                            search,
                                        },
                                    });
                                }}
                            >
                                <ListboxOption value={"all"}>
                                    <ListboxLabel>{t("label.show-all")}</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value={"server-only"}>
                                    <ServerStackIcon />
                                    <ListboxLabel>{t("label.show-server-only")}</ListboxLabel>
                                </ListboxOption>
                                <ListboxOption value={"clients-only"}>
                                    <ComputerDesktopIcon />
                                    <ListboxLabel>{t("label.show-clients-only")}</ListboxLabel>
                                </ListboxOption>
                            </Listbox>
                        </Field>
                    </FieldGroup>
                </Fieldset>
            </Form>

            <div className={"flex gap-3"}>
                <BadgeButton
                    color={"blue"}
                    href={"."}
                    search={({ filter, ...prev }) => ({
                        ...prev,
                        filter: filter === "server-only" ? "all" : "server-only",
                    })}
                >
                    {t("label.server", { count: data.total_servers })}
                </BadgeButton>
                <BadgeButton
                    color={"blue"}
                    href={"."}
                    search={({ filter, ...prev }) => ({
                        ...prev,
                        filter: filter === "clients-only" ? "all" : "clients-only",
                    })}
                >
                    {t("label.clients", { count: data.total_clients })}
                </BadgeButton>
            </div>

            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.device-name")}</TableHeader>
                        <TableHeader>{t("label.last-seen")}</TableHeader>
                        <TableHeader>{t("label.addresses")}</TableHeader>
                        <TableHeader className={"max-lg:hidden"}>{t("label.key-expiry")}</TableHeader>
                        <TableHeader className={"max-lg:hidden"}>{t("label.first-login")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((device) => (
                        <DeviceRowAdmin
                            key={device.uuid}
                            device={device}
                            setOpenDeleteDevice={setOpenDeleteDevice}
                            setOpenRenameDevice={setOpenRenameDevice}
                            setOpenExpireKey={setOpenExpireKey}
                        />
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/devices"}
                getSearchParams={(newPage: number) => ({ search, page: newPage, filter })}
            />

            {openRenameDevice && (
                <Suspense>
                    <RenameDeviceDialog
                        device={openRenameDevice}
                        onClose={() => setOpenRenameDevice(undefined)}
                        onRename={() => {
                            router.invalidate({ sync: true });
                            setOpenRenameDevice(undefined);
                        }}
                    />
                </Suspense>
            )}

            {openExpireKey && (
                <Suspense>
                    <ExpireDeviceKeyDialog
                        device={openExpireKey}
                        onClose={() => setOpenExpireKey(undefined)}
                        onExpire={() => {
                            router.invalidate({ sync: true });
                            setOpenExpireKey(undefined);
                        }}
                    />
                </Suspense>
            )}

            {openDeleteDevice && (
                <Suspense>
                    <DeleteDeviceDialog
                        onClose={() => setOpenDeleteDevice(undefined)}
                        onDeletion={() => {
                            router.invalidate({ sync: true });
                            setOpenDeleteDevice(undefined);
                        }}
                        device={openDeleteDevice}
                    />
                </Suspense>
            )}
        </HeadingLayout>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type DeviceOverviewSearch = {
    /** The current page number we're on */
    page: number;
    /** The search term that should be used */
    search: string;
    /** The type of filter to use */
    filter: Filter;
};

export const Route = createFileRoute("/_admin/a/devices/")({
    component: DeviceOverview,
    validateSearch: (search: Record<string, unknown>): DeviceOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return {
            page: page <= 0 ? 1 : page,
            search: (search.search as string) || "",
            filter: (search.filter as Filter) || "all",
        };
    },
    loaderDeps: ({ search: { page, search, filter } }) => ({ page, search, filter }),
    loader: async ({ deps }) =>
        await ApiInternal.devices.getAll({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
            filter_name: deps.search,
            filter_is_server: deps.filter === "all" ? undefined : deps.filter === "server-only",
        }),
});
